import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Redeem as RedeemIcon,
} from '@mui/icons-material';
import { format as formatDate, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  loyaltyRewardService,
  loyaltyCampaignService,
  type LoyaltyReward,
  type LoyaltyRewardStatus,
  type LoyaltyCampaign,
} from '../services';
import authService from '../services/authService';
import { useToast } from '../hooks/useToast';

type StatusFilter = 'all' | LoyaltyRewardStatus;

const STATUS_LABELS: Record<LoyaltyRewardStatus, string> = {
  PENDING: 'Pendente',
  REDEEMED: 'Resgatada',
  EXPIRED: 'Expirada',
};

const STATUS_COLOR: Record<LoyaltyRewardStatus, 'warning' | 'success' | 'default'> = {
  PENDING: 'warning',
  REDEEMED: 'success',
  EXPIRED: 'default',
};

const formatDateTime = (value?: string | Date) => {
  if (!value) return '-';
  try {
    const date = typeof value === 'string' ? parseISO(value) : value;
    return formatDate(date, "dd 'de' MMMM 'de' yyyy HH:mm", { locale: ptBR });
  } catch (error) {
    return typeof value === 'string' ? value : value.toISOString();
  }
};

const formatCurrency = (value?: number) => {
  if (typeof value !== 'number') return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const PRIMARY_BUTTON_SX = {
  backgroundColor: '#F2C94C',
  color: '#2F2A1D',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#E0B83D',
    boxShadow: 'none',
  },
};

const LoyaltyRewardsPage: React.FC = () => {
  const { showError, showSuccess, showInfo } = useToast();
  const userRole = authService.getUserRole();
  const isDeveloper = userRole === 'DEVELOPER';
  const defaultCnpj = authService.getActiveCompanyCnpj() ?? authService.getUser()?.cnpj ?? '';

  const [rewards, setRewards] = useState<LoyaltyReward[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [cnpjFilter, setCnpjFilter] = useState(isDeveloper ? '' : defaultCnpj);
  const [searchTerm, setSearchTerm] = useState('');

  const [campaigns, setCampaigns] = useState<LoyaltyCampaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [redeemTarget, setRedeemTarget] = useState<LoyaltyReward | null>(null);
  const [redeemLoading, setRedeemLoading] = useState(false);

  const sanitizedCnpj = useMemo(() => {
    if (!cnpjFilter) return undefined;
    const digits = cnpjFilter.replace(/\D/g, '');
    return digits.length > 0 ? digits : undefined;
  }, [cnpjFilter]);

  const loadCampaigns = useCallback(async () => {
    if (!isDeveloper && campaigns.length) {
      return;
    }
    try {
      setCampaignsLoading(true);
      const data = await loyaltyCampaignService.listCampaigns({
        cnpj: sanitizedCnpj,
      });
      setCampaigns(data);
    } catch (error: any) {
      console.error('[LoyaltyRewardsPage] loadCampaigns error', error);
      showError(error?.message || 'Não foi possível carregar as campanhas.');
    } finally {
      setCampaignsLoading(false);
    }
  }, [sanitizedCnpj, showError, isDeveloper, campaigns.length]);

  useEffect(() => {
    void loadCampaigns();
  }, [loadCampaigns]);

  const loadRewards = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const data = await loyaltyRewardService.list({
        status: statusFilter === 'all' ? undefined : statusFilter,
        campaignId: campaignFilter.trim() || undefined,
        customerId: customerFilter.trim() || undefined,
        cnpj: sanitizedCnpj,
      });
      setRewards(data);
      if (!data.length) {
        showInfo('Nenhuma recompensa encontrada para os filtros selecionados.');
      }
    } catch (error: any) {
      console.error('[LoyaltyRewardsPage] loadRewards error', error);
      const message = error?.message || 'Não foi possível carregar as recompensas.';
      setErrorMessage(message);
      showError(message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, campaignFilter, customerFilter, sanitizedCnpj, showError, showInfo]);

  useEffect(() => {
    void loadRewards();
  }, [loadRewards]);

  const filteredRewards = useMemo(() => {
    if (!searchTerm.trim()) return rewards;
    const term = searchTerm.trim().toLowerCase();
    return rewards.filter((reward) => {
      return (
        reward.id.toLowerCase().includes(term) ||
        reward.customerId.toLowerCase().includes(term) ||
        reward.campaignId.toLowerCase().includes(term) ||
        reward.couponCode?.toLowerCase().includes(term) ||
        reward.rewardDescription?.toLowerCase().includes(term)
      );
    });
  }, [rewards, searchTerm]);

  const handleOpenRedeemDialog = (reward: LoyaltyReward) => {
    setRedeemTarget(reward);
    setRedeemDialogOpen(true);
  };

  const handleCloseRedeemDialog = () => {
    if (redeemLoading) return;
    setRedeemDialogOpen(false);
    setRedeemTarget(null);
  };

  const handleRedeemReward = async () => {
    if (!redeemTarget) return;
    try {
      setRedeemLoading(true);
      const updated = await loyaltyRewardService.markRedeemed(redeemTarget.id, {
        cnpj: sanitizedCnpj,
      });
      setRewards((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      showSuccess('Recompensa marcada como resgatada.');
      handleCloseRedeemDialog();
    } catch (error: any) {
      console.error('[LoyaltyRewardsPage] handleRedeemReward error', error);
      showError(error?.message || 'Não foi possível marcar a recompensa como resgatada.');
    } finally {
      setRedeemLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#F5F6FA', minHeight: '100vh', py: { xs: 2, md: 3 } }}>
      <Container maxWidth="lg" sx={{ pb: { xs: 4, md: 6 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" mb={3} spacing={2}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Recompensas de Fidelidade
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Acompanhe cupons e benefícios gerados pelas campanhas e marque resgates manualmente quando necessário.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => void loadRewards()}
            disabled={isLoading}
            sx={{ ...PRIMARY_BUTTON_SX, alignSelf: { xs: 'stretch', md: 'auto' } }}
          >
            Atualizar lista
          </Button>
        </Stack>

        <Paper
          sx={{
            p: 2.5,
            mb: 3,
            backgroundColor: '#FFFFFF',
            borderRadius: 2,
            border: '1px solid #E4E7EC',
          }}
          elevation={0}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box
              sx={{
                flexBasis: { xs: '100%', md: '33%' },
                flexGrow: 1,
                minWidth: { xs: '100%', md: '260px' },
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Pesquisar"
                placeholder="ID, cliente, campanha ou cupom"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box
              sx={{
                flexBasis: { xs: '100%', md: '18%' },
                flexGrow: { xs: 1, md: 0 },
                minWidth: { xs: '100%', md: '180px' },
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  label="Status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="PENDING">Pendentes</MenuItem>
                  <MenuItem value="REDEEMED">Resgatadas</MenuItem>
                  <MenuItem value="EXPIRED">Expiradas</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box
              sx={{
                flexBasis: { xs: '100%', md: '23%' },
                flexGrow: { xs: 1, md: 0 },
                minWidth: { xs: '100%', md: '220px' },
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="campaign-filter-label">Campanha</InputLabel>
                <Select
                  labelId="campaign-filter-label"
                  label="Campanha"
                  value={campaignFilter}
                  onChange={(event) => setCampaignFilter(event.target.value)}
                  disabled={campaignsLoading && !campaigns.length}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box
              sx={{
                flexBasis: { xs: '100%', md: '23%' },
                flexGrow: { xs: 1, md: 0 },
                minWidth: { xs: '100%', md: '220px' },
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="ID do cliente"
                value={customerFilter}
                onChange={(event) => setCustomerFilter(event.target.value)}
              />
            </Box>
            {isDeveloper && (
              <Box
                sx={{
                  flexBasis: { xs: '100%', md: '23%' },
                  flexGrow: { xs: 1, md: 0 },
                  minWidth: { xs: '100%', md: '220px' },
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="CNPJ"
                  value={cnpjFilter}
                  onChange={(event) => setCnpjFilter(event.target.value)}
                  placeholder="Apenas números"
                />
              </Box>
            )}
          </Box>
        </Paper>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Paper sx={{ borderRadius: 2, border: '1px solid #E4E7EC', backgroundColor: '#FFFFFF' }} elevation={0}>
          <TableContainer>
            <Table>
            <TableHead>
              <TableRow>
                <TableCell>Recompensa</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Campanha</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Valor / Cupom</TableCell>
                <TableCell>Emitida</TableCell>
                <TableCell>Resgatada</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Carregando recompensas...
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : filteredRewards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma recompensa encontrada.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRewards.map((reward) => (
                  <TableRow key={reward.id} hover>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="subtitle2">{reward.id}</Typography>
                        {reward.rewardDescription && (
                          <Typography variant="caption" color="text.secondary">
                            {reward.rewardDescription}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{reward.customerId}</Typography>
                        {reward.couponCode && (
                          <Chip size="small" label={`Cupom: ${reward.couponCode}`} icon={<RedeemIcon fontSize="small" />} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{reward.campaignId}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[reward.status]}
                        color={STATUS_COLOR[reward.status]}
                        icon={reward.status === 'REDEEMED' ? <CheckCircleIcon fontSize="small" /> : undefined}
                      />
                    </TableCell>
                    <TableCell>
                      {reward.rewardType === 'COUPON'
                        ? formatCurrency(reward.couponValue)
                        : reward.rewardValue || '-'}
                    </TableCell>
                    <TableCell>{formatDateTime(reward.issuedAt)}</TableCell>
                    <TableCell>{formatDateTime(reward.redeemedAt)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Marcar como resgatada">
                        <span>
                          <IconButton
                            color="success"
                            disabled={reward.status !== 'PENDING'}
                            onClick={() => handleOpenRedeemDialog(reward)}
                          >
                            <RedeemIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </TableContainer>
        </Paper>

        <Dialog
          open={redeemDialogOpen}
          onClose={handleCloseRedeemDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              backgroundColor: '#FFFFFF',
              borderRadius: 2,
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
            },
          }}
        >
          <DialogTitle>Marcar recompensa como resgatada</DialogTitle>
          <DialogContent dividers sx={{ backgroundColor: '#F7F8FC' }}>
            <DialogContentText>
              Confirma o resgate manual desta recompensa? Essa ação registra a data atual como data de resgate e não pode ser desfeita.
            </DialogContentText>
            {redeemTarget && (
              <Box mt={2}>
                <Typography variant="subtitle2">Recompensa</Typography>
                <Typography variant="body1" gutterBottom>
                  {redeemTarget.rewardDescription || redeemTarget.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cliente: {redeemTarget.customerId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Campanha: {redeemTarget.campaignId}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ backgroundColor: '#F7F8FC' }}>
            <Button onClick={handleCloseRedeemDialog} disabled={redeemLoading}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => void handleRedeemReward()}
              disabled={redeemLoading}
            >
              {redeemLoading ? <CircularProgress size={18} color="inherit" /> : 'Confirmar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default LoyaltyRewardsPage;
